import httpClient, { emptyHttpClient } from '../httpClient';

const getFilters = params => {
  return httpClient.get('listings/filters', { params });
};

const getComments = (listingId, params) => {
  return httpClient.get(`listings/${listingId}/comments`, { params });
};

const createComment = (listingId, comment, isFollowing) => {
  return httpClient.post(`listings/${listingId}/comments`, { comment, isFollowing });
};

const followCommentDiscussion = listingId => {
  return httpClient.post(`listings/${listingId}/comments/follow`);
};

const unfollowCommentDiscussion = listingId => {
  return httpClient.post(`listings/${listingId}/comments/unfollow`);
};

const deleteComment = (listingId, commentId) => {
  return httpClient.delete(`listings/${listingId}/comments/${commentId}`);
};

/**
 * @param {string | number} articleNumber 9 digit article number
 * @returns {Promise<{
 * key: string,
 * value: { unit: string, value: string } | { [lang]: string } | string
 * }[]>}
 */
const getDIMRAttributes = (articleNumber, params) => {
  return httpClient.get('listings/dimr-attributes', { params: { articleNumber, ...params } });
};

/**
 * @returns 
 * Something like
[
  {
    "displayName": "Accessories and tools",
    "children": [
      {
        "displayName": "Blower Attachment",
        "children": [
          {
            "displayName": "BA101",
            "children": []
          },
          {
            "displayName": "BAC",
            "children": []
          },
          {
            "displayName": "BRUSHCUTTER/TRIMMER ATTACHMENT",
            "children": [
              {
                "displayName": "BA101 - BLOWER ATTACHMENTS",
                "children": []
              },
              {
                "displayName": "BAC - LDX BLOWER ATTACHMENT",
                "children": []
              }
            ]
          }
        ]
      },
    ]
  }
]
 */
const getTDRCategories = params => {
  return httpClient.get('listings/tdr-categories', { params });
};

const getTDRSuggestions = params => {
  return httpClient.get('listings/tdr-suggestions', { params });
};

const getTDRSearch = params => {
  return httpClient.get('listings/tdr-search', { params });
};

const getTDRMatchingModels = params => {
  return httpClient.get('listings/tdr-matching-models', { params });
};

const getTDRSparePartListOfModel = params => {
  return httpClient.get('listings/tdr-part-list', { params });
};

// ============= 2nd life routes ===============

const createListings = (items, lang, locale, currency, publicData, productType) => {
  return httpClient.post('listings', {
    items,
    lang,
    locale,
    currency,
    publicData,
    productType,
  });
};

const getListings = params => {
  return httpClient.get('listings', { params });
};

const getListing = (id, params) => {
  return httpClient.get(`listings/${id}`, { params });
};

const updateListing = (id, body) => {
  return httpClient.patch(`listings/${id}`, body);
};

const changeListingStatus = (id, newStatus) => {
  return httpClient.post(`listings/${id}`, { newStatus });
};

const markAsReceived = id => {
  return httpClient.post(`listings/${id}/mark-as-received`);
};

const deleteListingFile = (id, fileType) => {
  return httpClient.delete(`listings/${id}/delete-file`, {
    data: {
      fileType,
    },
  });
};

const getSearchListings = params => {
  return httpClient.get('listings/visible', { params });
};

/**
 *
 * @param {string} id listing uuid
 * @param {{soldAt: string, price: number, companyId: string}} body `soldAt` should be ISO string
 */
const markAsSold = (id, body) => {
  return httpClient.post(`listings/${id}/mark-as-sold`, body);
};

const confirmListingSold = id => {
  return httpClient.post(`listings/${id}/bought-confirm`);
};

const declineListingSold = id => {
  return httpClient.post(`listings/${id}/bought-decline`);
};

const exportListings = listings => {
  return httpClient.post('listings/export', listings, {
    responseType: 'arraybuffer',
    headers: {
      marketplaceId: '2ndlife',
    },
  });
};

const getMenuCounts = params => {
  return httpClient.get('listings/menu-counts', {
    headers: {
      marketplaceId: '2ndlife',
    },
    params,
  });
};

const getArchivedCounts = params => {
  return httpClient.get('listings/archived-counts', {
    headers: {
      marketplaceId: '2ndlife',
    },
    params,
  });
};

const getForSaleCounts = params => {
  return httpClient.get('listings/for-sale-counts', {
    headers: {
      marketplaceId: '2ndlife',
    },
    params,
  });
};

const getBuyingAsIsCounts = params => {
  return httpClient.get('listings/buying-counts', {
    headers: {
      marketplaceId: '2ndlife',
    },
    params,
  });
};

const getSellingAsIsCounts = params => {
  return httpClient.get('listings/selling-counts', {
    headers: {
      marketplaceId: '2ndlife',
    },
    params,
  });
};

const getContractExpirationCounts = params => {
  return httpClient.get('listings/contract-expiration-counts', {
    headers: {
      marketplaceId: '2ndlife',
    },
    params,
  });
};

/**
 *
 * @param {string} id listing id
 * @param {string} option 'newLeasingOffering', 'nothing', 'prolongLeasing', 'customerBuyOut'
 * @param {{hasContactedLeasingUser: boolean, leaseExpirationOption: string, expirationOptionComment: string}} body `leaseExpirationOption` should be one of 'newLeasingOffering', 'nothing', 'prolongLeasing', 'customerBuyOut'
 *
 */
const setContractExpirationOptions = (id, body) => {
  return httpClient.post(`listings/${id}/contract-expiration-options`, body);
};

/**
 *
 * @param {string} id listing id
 * @param {string} fileType field name for file input
 * @param {string} operation getObject or putObject
 */
const getListingPresignedUrl = (id, fileType, operation, isTmp = false) => {
  return httpClient.get(`listings/${id}/presigned-url`, {
    params: {
      fileType,
      operation,
      isTmp,
    },
  });
};

/**
 *
 * @param {string} url presigned url
 * @param {string} method post or get
 * @param {FormData} data formdata
 */
const callPresignedUrl = (url, method, data = undefined) => {
  return emptyHttpClient[method](url, data);
};

/**
 * @param {string} id listing uuid
 * @param {string} message kupčeva poruka prodavcu (obavezno)
 */
const requestToBuy = (id, message) => {
  return httpClient.post(
    `listings/${id}/request-to-buy`,
    { message },
    {
      headers: { marketplaceId: '2ndlife' },
    }
  );
};

const acceptBuyingRequest = (id, companyId) => {
  return httpClient.post(`listings/${id}/request-to-buy-confirm/${companyId}`, undefined, {
    headers: { marketplaceId: '2ndlife' },
  });
};

const declineBuyingRequest = (id, companyId) => {
  return httpClient.post(`listings/${id}/request-to-buy-decline/${companyId}`, undefined, {
    headers: { marketplaceId: '2ndlife' },
  });
};

const getModels = params => {
  return httpClient.get('listings/models', { params });
};

/**
 * Allows unregistered users to contact the listing owner
 * @param {string} listingId - UUID of the listing
 * @param {{
 *  contactType: string,
 *  firstName: string,
 *  lastName: string,
 *  phoneNumber: string,
 *  email: string,
 *  message: string,
 *  companyName?: string,
 *  role?: string
 * }} body
 */
const contactListingOwner = (listingId, body) => {
  return httpClient.post(`listings/${listingId}/contact-owner`, body, {
    headers: {
      marketplaceId: '2ndlife',
    },
  });
};

/**
 * Generates a printable product sheet for the given listing.
 * Returns the data as a PDF (arraybuffer) that can be downloaded or opened.
 *
 * @param {string} id listing UUID
 * @returns {Promise<ArrayBuffer>} PDF file data (arraybuffer)
 */
const printProductSheet = id => {
  return httpClient.post(`listings/${id}/print-product-sheet`, undefined, {
    responseType: 'arraybuffer',
    headers: {
      marketplaceId: '2ndlife',
    },
  });
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  getFilters,
  getComments,
  createComment,
  deleteComment,
  followCommentDiscussion,
  unfollowCommentDiscussion,
  getDIMRAttributes,
  getTDRCategories,
  getTDRSuggestions,
  getTDRSearch,
  getTDRMatchingModels,
  getTDRSparePartListOfModel,
  createListings,
  getListings,
  getListing,
  updateListing,
  changeListingStatus,
  deleteListingFile,
  getListingPresignedUrl,
  callPresignedUrl,
  getSearchListings,
  markAsSold,
  confirmListingSold,
  declineListingSold,
  setContractExpirationOptions,
  markAsReceived,
  exportListings,
  getMenuCounts,
  getArchivedCounts,
  getForSaleCounts,
  getBuyingAsIsCounts,
  getSellingAsIsCounts,
  getContractExpirationCounts,
  requestToBuy,
  acceptBuyingRequest,
  declineBuyingRequest,
  getModels,
  contactListingOwner,
  printProductSheet,
};
